import * as React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  IconCheck,
  IconClock,
  IconDots,
  IconChevronDown,
  IconDownload,
  IconSend,
  IconCopy,
  IconTrash,
  IconSignature,
  IconReceipt,
  IconWriting,
  IconFileInvoice,
  IconReceipt2,
  IconClipboardCheck,
  IconPrinter,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

export type DealDocumentTimelineDocType =
  | "quote"
  | "deposit_invoice"
  | "tax_document"
  | "invoice"
  | "contract_zmluva"
  | "contract_protocol";

export interface TimelineOffer {
  id: string;
  number: string;
  /** Optional human-readable name/description shown below the number in the info panel. */
  description?: string;
  date: string;
  amount: number;
  status: "accepted" | "declined" | "open";
}

export interface TimelineZalohovaFaktura {
  id: string;
  number: string;
  date: string;
  amount: number;
  paid: boolean;
  /** CRM quote row id this deposit was generated from (active chain quote). */
  generatedFromQuoteId?: string;
}

export interface TimelineZmluva {
  id: string;
  date: string;
  signed: boolean;
}

export interface TimelineDanovyDoklad {
  id: string;
  number: string;
  date: string;
  amount: number;
}

export interface TimelineKonecnaFaktura {
  id: string;
  number: string;
  date: string;
  amount: number;
}

export interface TimelineOdovzdavaciProtokol {
  id: string;
  date: string;
}

export interface DealDocumentTimelineProps {
  offers: TimelineOffer[];
  /** Ponuka whose document chain is shown in steps 2–6 (badge in list). */
  activeOfferQuoteId?: string | null;
  /** ID of the manually chosen anchor quote (shows Anchor badge + Zmeniť action). */
  anchorQuoteId?: string | null;
  /** Called when user picks "Stanoviť ako hlavnú PON" for a non-anchor offer. */
  onSetAnchor?: (id: string) => void;
  /** Called when user clicks "Zmeniť hlavnú PON" on the anchor offer row. */
  onChangeAnchor?: () => void;
  zalohovaFaktura?: TimelineZalohovaFaktura;
  zmluva?: TimelineZmluva;
  danovyDoklad?: TimelineDanovyDoklad;
  konecnaFaktura?: TimelineKonecnaFaktura;
  odovzdavaciProtokol?: TimelineOdovzdavaciProtokol;
  onDocumentOpen: (type: DealDocumentTimelineDocType, id: string) => void;
  onDocumentDownload: (type: DealDocumentTimelineDocType, id: string) => void;
  onDocumentEmail: (type: DealDocumentTimelineDocType, id: string) => void;
  onDocumentDelete: (type: DealDocumentTimelineDocType, id: string) => void;
  onOfferDuplicate: (id: string) => void;
  /** Called when user picks "Generovať zmluvu" from the zálohová faktúra menu. */
  onGenerateZmluva?: () => void;
  onGenerateZalohovaFaktura?: () => void;
  onGenerateDanovyDoklad?: () => void;
  onGenerateKonecnaFaktura?: () => void;
  onGenerateOdovzdavaciProtokol?: () => void;
  /**
   * Rich “Ďalší krok” block (deposit picker, final invoice, etc.).
   * Rendered at the bottom of the right column. When set, the compact Card 3
   * callback button is hidden so parent CTAs are not duplicated.
   */
  nextStepSlot?: React.ReactNode;
  /** Print all 6 chain PDFs (quote → protocol). Only called when the chain is complete. */
  onPrintAllChain?: () => void;
  /** True while PDFs are being fetched / print dialogs are open. */
  printAllPending?: boolean;
  /** Override button label while printing (e.g. "PDF-ek betöltése… 3/6"). */
  printAllProgressLabel?: string | null;
}

// ─── Step state helpers ────────────────────────────────────────────────────────

type StepState = "done" | "active" | "future";

function fmt(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("sk-SK", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function fmtEur(amount: number): string {
  return `${amount.toFixed(2).replace(".", ",")} €`;
}

// ─── Dot components ────────────────────────────────────────────────────────────

function StepDot({ state }: { state: StepState }) {
  const base = "relative z-10 flex items-center justify-center rounded-full shrink-0";
  if (state === "done") {
    return (
      <span
        className={cn(base, "w-[26px] h-[26px] bg-[#aa3839] text-white")}
        aria-label="done"
      >
        <IconCheck size={13} stroke={2.5} />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span
        className={cn(
          base,
          "w-[26px] h-[26px] bg-white border-2 border-[#aa3839] text-[#aa3839]",
          "shadow-[0_0_0_4px_rgba(170,56,57,0.12)]",
        )}
        aria-label="active"
      >
        <IconClock size={12} stroke={2} />
      </span>
    );
  }
  // future
  return (
    <span
      className={cn(base, "w-[26px] h-[26px] bg-background border-2 border-border opacity-40")}
      aria-label="future"
    />
  );
}

function OfferDot({ status }: { status: TimelineOffer["status"] }) {
  const base = "flex items-center justify-center rounded-full shrink-0";
  if (status === "accepted") {
    return (
      <span className={cn(base, "w-[14px] h-[14px] bg-[#1D9E75] text-white")}>
        <IconCheck size={8} stroke={3} />
      </span>
    );
  }
  if (status === "declined") {
    return (
      <span className={cn(base, "w-[14px] h-[14px] bg-muted border border-border")} />
    );
  }
  return (
    <span className={cn(base, "w-[14px] h-[14px] border-2 border-muted-foreground/40")} />
  );
}

// ─── Connector ────────────────────────────────────────────────────────────────

function Connector({ state }: { state: "solid" | "dashed" }) {
  if (state === "solid") {
    return <div className="w-[2px] h-full bg-[#aa3839] mx-auto" />;
  }
  return (
    <div
      className="mx-auto"
      style={{
        width: 2,
        height: "100%",
        backgroundImage: "repeating-linear-gradient(to bottom, #d1d5db 0, #d1d5db 5px, transparent 5px, transparent 9px)",
      }}
    />
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

interface BadgeProps {
  label: string;
  variant: "done" | "active" | "neutral";
}

function StepBadge({ label, variant }: BadgeProps) {
  if (variant === "done") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#E1F5EE] text-[#0F6E56]">
        {label}
      </span>
    );
  }
  if (variant === "active") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#f9eded] text-[#aa3839]">
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground">
      {label}
    </span>
  );
}

function OfferBadge({ status }: { status: TimelineOffer["status"] }) {
  if (status === "accepted") {
    return <StepBadge label="Prijatá" variant="done" />;
  }
  if (status === "declined") {
    return <StepBadge label="Zamietnutá" variant="neutral" />;
  }
  return <StepBadge label="Otvorená" variant="neutral" />;
}

// ─── Action menu ──────────────────────────────────────────────────────────────

interface ActionMenuProps {
  menuKey: string;
  openKey: string | null;
  onOpenChange: (key: string | null) => void;
  items: React.ReactNode;
}

function ActionMenu({ menuKey, openKey, onOpenChange, items }: ActionMenuProps) {
  return (
    <DropdownMenu
      open={openKey === menuKey}
      onOpenChange={(open) => onOpenChange(open ? menuKey : null)}
    >
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center justify-center w-7 h-7 rounded border border-border bg-background text-foreground/60 hover:text-foreground hover:bg-accent transition-colors shrink-0"
          aria-label="Akcie"
          onClick={(e) => e.stopPropagation()}
        >
          <IconDots size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[160px]"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {items}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Row container ────────────────────────────────────────────────────────────

function StepRow({
  state,
  children,
  onClick,
  className,
}: {
  state: StepState;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  const clickable = state !== "future" && !!onClick;
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => (e.key === "Enter" || e.key === " ") && onClick?.()
          : undefined
      }
      className={cn(
        "flex items-center gap-3 rounded px-2 py-1.5 -mx-2 transition-colors",
        clickable && "cursor-pointer hover:bg-[hsl(var(--color-background-secondary))]",
        state === "future" && "opacity-40 pointer-events-none select-none",
        className,
      )}
    >
      {children}
    </div>
  );
}

// ─── Single-document step row ─────────────────────────────────────────────────

interface SingleStepProps {
  state: StepState;
  docType: DealDocumentTimelineDocType;
  title: string;
  subtitle: string;
  number?: string;
  date?: string;
  amount?: number;
  badgeLabel?: string;
  isLast: boolean;
  menuKey: string;
  openMenuKey: string | null;
  onOpenMenuChange: (key: string | null) => void;
  onOpen?: () => void;
  onDownload?: () => void;
  onEmail?: () => void;
  onDelete?: () => void;
  onGenerateZmluva?: () => void;
}

function SingleStep({
  state,
  title,
  subtitle,
  number,
  date,
  amount,
  badgeLabel,
  isLast,
  menuKey,
  openMenuKey,
  onOpenMenuChange,
  onOpen,
  onDownload,
  onEmail,
  onDelete,
  onGenerateZmluva,
}: SingleStepProps) {
  const connectorStyle: "solid" | "dashed" =
    state === "done" ? "solid" : "dashed";

  return (
    <div className="flex gap-3">
      {/* Left: dot + connector */}
      <div className="flex flex-col items-center" style={{ width: 26 }}>
        <StepDot state={state} />
        {!isLast && (
          <div className="flex-1 py-1" style={{ minHeight: 24 }}>
            <Connector state={connectorStyle} />
          </div>
        )}
      </div>

      {/* Right: content */}
      <div className="flex-1 min-w-0 pb-4">
        <StepRow state={state} onClick={state !== "future" ? onOpen : undefined}>
          <div className="flex-1 min-w-0">
            {number ? (
              <>
                <div className="text-[13px] font-semibold leading-tight truncate">{number}</div>
                <div className="text-[11px] text-muted-foreground truncate">{subtitle}</div>
              </>
            ) : (
              <div className="text-[13px] text-muted-foreground">{title}</div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {badgeLabel && state !== "future" && (
              <StepBadge
                label={badgeLabel}
                variant={state === "done" ? "done" : "active"}
              />
            )}
            {amount != null && state !== "future" && (
              <span className="text-[12px] font-medium">{fmtEur(amount)}</span>
            )}
            {date && state !== "future" && (
              <span className="text-[11px] text-muted-foreground">{fmt(date)}</span>
            )}
            {state !== "future" && onDownload && (
              <ActionMenu
                menuKey={menuKey}
                openKey={openMenuKey}
                onOpenChange={onOpenMenuChange}
                items={
                  <>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownload();
                      }}
                    >
                      <IconDownload size={13} className="mr-2" />
                      Stiahnuť PDF
                    </DropdownMenuItem>
                    {onEmail && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onEmail();
                        }}
                      >
                        <IconSend size={13} className="mr-2" />
                        Odoslať emailom
                      </DropdownMenuItem>
                    )}
                    {onGenerateZmluva && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onGenerateZmluva();
                          }}
                        >
                          <IconSignature size={13} className="mr-2" />
                          Generovať zmluvu
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.();
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <IconTrash size={13} className="mr-2" />
                      Odstrániť
                    </DropdownMenuItem>
                  </>
                }
              />
            )}
          </div>
        </StepRow>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function DealDocumentTimeline({
  offers,
  activeOfferQuoteId,
  anchorQuoteId,
  onSetAnchor,
  onChangeAnchor,
  zalohovaFaktura,
  zmluva,
  danovyDoklad,
  konecnaFaktura,
  odovzdavaciProtokol,
  onDocumentOpen,
  onDocumentDownload,
  onDocumentEmail,
  onDocumentDelete,
  onOfferDuplicate,
  onGenerateZmluva,
  onGenerateZalohovaFaktura,
  onGenerateDanovyDoklad,
  onGenerateKonecnaFaktura,
  onGenerateOdovzdavaciProtokol,
  nextStepSlot,
  onPrintAllChain,
  printAllPending = false,
  printAllProgressLabel = null,
}: DealDocumentTimelineProps) {
  const { t } = useTranslation();
  /** Default open so every quote is visible without an extra click. */
  const [offersExpanded, setOffersExpanded] = useState(true);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  const activeQuoteIdForPrint =
    zalohovaFaktura?.generatedFromQuoteId ||
    (anchorQuoteId && offers.some((o) => o.id === anchorQuoteId) ? anchorQuoteId : null) ||
    activeOfferQuoteId ||
    null;

  const canPrintAllChain = !!(
    activeQuoteIdForPrint &&
    zalohovaFaktura &&
    zmluva &&
    danovyDoklad &&
    konecnaFaktura &&
    odovzdavaciProtokol &&
    onPrintAllChain
  );

  // ── Derive sequential step states ─────────────────────────────────────────
  const step1Done = offers.length > 0;
  // A daňový doklad can only exist once the deposit is paid → its presence implies paid.
  const step2Done = !!danovyDoklad || !!zalohovaFaktura?.paid;
  const step3Done = !!zmluva?.signed;
  const step4Done = !!danovyDoklad;
  const step5Done = !!konecnaFaktura;
  const step6Done = !!odovzdavaciProtokol;

  function deriveState(myDone: boolean, prevDone: boolean): StepState {
    if (myDone) return "done";
    if (prevDone) return "active";
    return "future";
  }

  const s1: StepState = step1Done ? "done" : "active"; // step 1 is always at least active
  const s2 = deriveState(step2Done, step1Done);
  const s3 = deriveState(step3Done, step2Done);
  const s4 = deriveState(step4Done, step3Done);
  const s5 = deriveState(step5Done, step4Done);
  const s6 = deriveState(step6Done, step5Done);

  function connectorBetween(upper: StepState, lower: StepState): "solid" | "dashed" {
    return upper === "done" && lower === "done" ? "solid" : "dashed";
  }

  const acceptedOffer = offers.find((o) => o.status === "accepted");
  const depositChainQuoteId = zalohovaFaktura?.generatedFromQuoteId;
  /** Offer shown as “active / accepted” in the list (anchor, záloha chain, SF status). */
  const panelActiveOffer =
    (anchorQuoteId ? offers.find((o) => o.id === anchorQuoteId) : undefined) ||
    (depositChainQuoteId && offers.find((o) => o.id === depositChainQuoteId)) ||
    acceptedOffer ||
    (activeOfferQuoteId ? offers.find((o) => o.id === activeOfferQuoteId) : undefined) ||
    null;
  const headerAmountOffer =
    (activeOfferQuoteId && offers.find((o) => o.id === activeOfferQuoteId)) ||
    acceptedOffer ||
    null;

  // ── Right panel derived state (pure, no new React state) ──────────────────
  const completedSteps = [
    zalohovaFaktura,
    zmluva,
    danovyDoklad,
    konecnaFaktura,
    odovzdavaciProtokol,
  ].filter(Boolean).length;

  type PipelineStep =
    | "zalohovaFaktura"
    | "zmluva"
    | "danovyDoklad"
    | "konecnaFaktura"
    | "odovzdavaciProtokol"
    | "completed";

  const nextStep: PipelineStep = !zalohovaFaktura
    ? "zalohovaFaktura"
    : !zmluva
      ? "zmluva"
      : !danovyDoklad
        ? "danovyDoklad"
        : !konecnaFaktura
          ? "konecnaFaktura"
          : !odovzdavaciProtokol
            ? "odovzdavaciProtokol"
            : "completed";

  const nextStepLabel: Record<PipelineStep, string> = {
    zalohovaFaktura: "Zálohová faktúra",
    zmluva: "Zmluva o dielo",
    danovyDoklad: "Daňový doklad",
    konecnaFaktura: "Konečná faktúra",
    odovzdavaciProtokol: "Odovzdávací protokol",
    completed: "Dokončené ✓",
  };

  const nextStepCallback: Record<Exclude<PipelineStep, "completed">, (() => void) | undefined> = {
    zalohovaFaktura: onGenerateZalohovaFaktura,
    zmluva: onGenerateZmluva,
    danovyDoklad: onGenerateDanovyDoklad,
    konecnaFaktura: onGenerateKonecnaFaktura,
    odovzdavaciProtokol: onGenerateOdovzdavaciProtokol,
  };

  const nextStepIcon: Record<Exclude<PipelineStep, "completed">, React.ReactNode> = {
    zalohovaFaktura: <IconReceipt size={18} stroke={2} />,
    zmluva: <IconWriting size={18} stroke={2} />,
    danovyDoklad: <IconFileInvoice size={18} stroke={2} />,
    konecnaFaktura: <IconReceipt2 size={18} stroke={2} />,
    odovzdavaciProtokol: <IconClipboardCheck size={18} stroke={2} />,
  };

  const ctaCallback = nextStep !== "completed" ? nextStepCallback[nextStep] : undefined;

  const declinedForPanel = depositChainQuoteId
    ? offers.filter((o) => o.id !== depositChainQuoteId).length
    : offers.filter((o) => o.status === "declined").length;

  return (
    <div className="space-y-3">
      {onPrintAllChain && (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canPrintAllChain || printAllPending}
                    onClick={() => {
                      if (canPrintAllChain) onPrintAllChain();
                    }}
                    className="gap-1.5"
                  >
                    {printAllPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <IconPrinter size={16} stroke={2} />
                    )}
                    {printAllPending
                      ? printAllProgressLabel ||
                        t("documents.printAllChainLoading", "Nyomtatás…")
                      : t("documents.printAllChain", "Összes dokumentum nyomtatása")}
                  </Button>
                </span>
              </TooltipTrigger>
              {!canPrintAllChain && !printAllPending && (
                <TooltipContent side="bottom" className="max-w-xs">
                  {t(
                    "documents.printAllChainDisabledHint",
                    "Mind a 6 dokumentumnak léteznie kell (árajánlat, záloha, zmluva, daňový doklad, faktúra, protokol).",
                  )}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

    <div className="grid grid-cols-1 gap-8 items-start min-[901px]:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="min-w-0">
    <div className="space-y-0 py-2">
      {/* ── Step 1: Cenové ponuky (collapsible; lists every offer in `offers`) ─ */}
      <div className="flex gap-3">
        <div className="flex flex-col items-center" style={{ width: 26 }}>
          <StepDot state={s1} />
          <div className="flex-1 py-1" style={{ minHeight: 24 }}>
            <Connector state={connectorBetween(s1, s2)} />
          </div>
        </div>

        <div className="flex-1 min-w-0 pb-3">
          <StepRow
            state={s1}
            onClick={() => setOffersExpanded((v) => !v)}
            className="cursor-pointer"
          >
            <div className="flex-1 min-w-0">
              <span className="text-[13px] font-semibold">{t("contracts.quote", "Cenové ponuky")}</span>
              {offers.length > 0 && (
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-muted text-muted-foreground">
                  {offers.length} ks
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {headerAmountOffer && (
                <span className="text-[12px] font-medium text-muted-foreground">
                  {fmtEur(headerAmountOffer.amount)}
                </span>
              )}
              <IconChevronDown
                size={14}
                className={cn(
                  "text-muted-foreground transition-transform",
                  offersExpanded && "rotate-180",
                )}
              />
            </div>
          </StepRow>

          {offersExpanded && offers.length > 0 && (
            <div
              className="mt-1 ml-1 space-y-0.5"
              style={{
                borderLeft: "2px solid hsl(var(--color-border-tertiary))",
                paddingLeft: 14,
              }}
            >
              {offers.map((offer) => {
                const depositSourceId = zalohovaFaktura?.generatedFromQuoteId;
                const isDepositSource = !!depositSourceId && offer.id === depositSourceId;
                const isAnchor = !!anchorQuoteId && offer.id === anchorQuoteId;
                const isActiveChain =
                  !!activeOfferQuoteId && offer.id === activeOfferQuoteId;

                // Chain-based display status: if a deposit exists in this chain,
                // the source quote is "accepted" and all others are "declined".
                const displayStatus: TimelineOffer["status"] = depositSourceId
                  ? isDepositSource
                    ? "accepted"
                    : "declined"
                  : "open";

                const isAccepted = displayStatus === "accepted";
                const isDeclined = displayStatus === "declined";
                // Hide "Zdroj zálohy" when that same quote already carries the "Kotva" badge.
                const showDepositSourceBadge = isDepositSource && !isAnchor;
                const menuKey = `offer-${offer.id}`;
                return (
                  <div
                    key={offer.id}
                    className={cn(
                      "flex items-center gap-2 rounded px-2 py-1.5 -mx-2 group transition-colors",
                      "hover:bg-[hsl(var(--color-background-secondary))] cursor-pointer",
                      isAccepted && "border-l-2 border-l-[#1D9E75] pl-[10px]",
                      isDeclined && "opacity-60",
                    )}
                    onClick={() => onDocumentOpen("quote", offer.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      (e.key === "Enter" || e.key === " ") &&
                      onDocumentOpen("quote", offer.id)
                    }
                  >
                    <OfferDot status={displayStatus} />
                    <div className="flex-1 min-w-0">
                      <span
                        className={cn(
                          "text-[12px] font-medium",
                          isDeclined && "line-through text-muted-foreground",
                        )}
                      >
                        {offer.number}
                      </span>
                      <div className="text-[11px] text-muted-foreground">
                        {fmt(offer.date)} · {fmtEur(offer.amount)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isAnchor && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-blue-500/10 text-blue-600 border border-blue-500/20">
                          {t("documents.anchorBadge", "Kotva")}
                        </span>
                      )}
                      {!isAnchor && isActiveChain && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-primary/10 text-primary border border-primary/20">
                          {t("documents.activeOfferBadge", "Aktívna")}
                        </span>
                      )}
                      {showDepositSourceBadge && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold tracking-wide bg-[#aa3839]/10 text-[#aa3839] border border-[#aa3839]/25 max-w-[11rem] truncate"
                          title={t(
                            "documents.depositSourceQuoteTitle",
                            "Zálohová faktúra patrí k tejto cenovej ponuke.",
                          )}
                        >
                          {t("documents.depositSourceQuoteBadge", "Zdroj zálohy")}
                        </span>
                      )}
                      <OfferBadge status={displayStatus} />
                      <ActionMenu
                        menuKey={menuKey}
                        openKey={openMenuKey}
                        onOpenChange={setOpenMenuKey}
                        items={
                          <>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onDocumentDownload("quote", offer.id);
                              }}
                            >
                              <IconDownload size={13} className="mr-2" />
                              Stiahnuť PDF
                            </DropdownMenuItem>
                            {isAccepted && (
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDocumentEmail("quote", offer.id);
                                }}
                              >
                                <IconSend size={13} className="mr-2" />
                                Odoslať emailom
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onOfferDuplicate(offer.id);
                              }}
                            >
                              <IconCopy size={13} className="mr-2" />
                              Duplikovať
                            </DropdownMenuItem>
                            {!isAnchor && onSetAnchor && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onSetAnchor(offer.id);
                                  }}
                                >
                                  {t("documents.markAsAnchor", "Stanoviť ako hlavnú PON")}
                                </DropdownMenuItem>
                              </>
                            )}
                            {isAnchor && onChangeAnchor && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onChangeAnchor();
                                  }}
                                >
                                  {t("documents.changeAnchor", "Zmeniť hlavnú PON")}
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onDocumentDelete("quote", offer.id);
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <IconTrash size={13} className="mr-2" />
                              Odstrániť
                            </DropdownMenuItem>
                          </>
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Step 2: Zálohová faktúra ─────────────────────────────────────── */}
      <SingleStep
        state={s2}
        docType="deposit_invoice"
        title="Zálohová faktúra"
        subtitle="Zálohová faktúra"
        number={zalohovaFaktura?.number}
        date={zalohovaFaktura?.date}
        amount={zalohovaFaktura?.amount}
        badgeLabel={
          s2 === "done" ? "Uhradená" : s2 === "active" ? "Čaká sa" : undefined
        }
        isLast={false}
        menuKey="deposit_invoice"
        openMenuKey={openMenuKey}
        onOpenMenuChange={setOpenMenuKey}
        onOpen={
          zalohovaFaktura
            ? () => onDocumentOpen("deposit_invoice", zalohovaFaktura.id)
            : undefined
        }
        onDownload={
          zalohovaFaktura
            ? () => onDocumentDownload("deposit_invoice", zalohovaFaktura.id)
            : undefined
        }
        onEmail={
          zalohovaFaktura
            ? () => onDocumentEmail("deposit_invoice", zalohovaFaktura.id)
            : undefined
        }
        onDelete={
          zalohovaFaktura
            ? () => onDocumentDelete("deposit_invoice", zalohovaFaktura.id)
            : undefined
        }
        onGenerateZmluva={onGenerateZmluva}
      />

      {/* ── Step 3: Zmluva o dielo ───────────────────────────────────────── */}
      <SingleStep
        state={s3}
        docType="contract_zmluva"
        title="Zmluva o dielo"
        subtitle="Zmluva o dielo"
        number={zmluva ? "Zmluva o dielo" : undefined}
        date={zmluva?.date}
        badgeLabel={
          s3 === "done" ? "Podpísaná" : s3 === "active" ? "Čaká sa" : undefined
        }
        isLast={false}
        menuKey="contract_zmluva"
        openMenuKey={openMenuKey}
        onOpenMenuChange={setOpenMenuKey}
        onOpen={
          zmluva ? () => onDocumentOpen("contract_zmluva", zmluva.id) : undefined
        }
        onDownload={
          zmluva
            ? () => onDocumentDownload("contract_zmluva", zmluva.id)
            : undefined
        }
        onEmail={
          zmluva ? () => onDocumentEmail("contract_zmluva", zmluva.id) : undefined
        }
        onDelete={
          zmluva
            ? () => onDocumentDelete("contract_zmluva", zmluva.id)
            : undefined
        }
      />

      {/* ── Step 4: Daňový doklad ─────────────────────────────────────────── */}
      <SingleStep
        state={s4}
        docType="tax_document"
        title="Daňový doklad k prijatej platbe"
        subtitle="Daňový doklad k prijatej platbe"
        number={danovyDoklad?.number}
        date={danovyDoklad?.date}
        amount={danovyDoklad?.amount}
        badgeLabel={
          s4 === "done"
            ? t("documents.taxDocBadgeDone", "Uhradená")
            : s4 === "active"
              ? "Čaká sa"
              : undefined
        }
        isLast={false}
        menuKey="tax_document"
        openMenuKey={openMenuKey}
        onOpenMenuChange={setOpenMenuKey}
        onOpen={
          danovyDoklad
            ? () => onDocumentOpen("tax_document", danovyDoklad.id)
            : undefined
        }
        onDownload={
          danovyDoklad
            ? () => onDocumentDownload("tax_document", danovyDoklad.id)
            : undefined
        }
        onEmail={
          danovyDoklad
            ? () => onDocumentEmail("tax_document", danovyDoklad.id)
            : undefined
        }
        onDelete={
          danovyDoklad
            ? () => onDocumentDelete("tax_document", danovyDoklad.id)
            : undefined
        }
      />

      {/* ── Step 5: Konečná faktúra ──────────────────────────────────────── */}
      <SingleStep
        state={s5}
        docType="invoice"
        title="Konečná faktúra"
        subtitle="Konečná faktúra"
        number={konecnaFaktura?.number}
        date={konecnaFaktura?.date}
        amount={konecnaFaktura?.amount}
        badgeLabel={
          s5 === "done" ? "Uhradená" : s5 === "active" ? "Čaká sa" : undefined
        }
        isLast={false}
        menuKey="invoice"
        openMenuKey={openMenuKey}
        onOpenMenuChange={setOpenMenuKey}
        onOpen={
          konecnaFaktura
            ? () => onDocumentOpen("invoice", konecnaFaktura.id)
            : undefined
        }
        onDownload={
          konecnaFaktura
            ? () => onDocumentDownload("invoice", konecnaFaktura.id)
            : undefined
        }
        onEmail={
          konecnaFaktura
            ? () => onDocumentEmail("invoice", konecnaFaktura.id)
            : undefined
        }
        onDelete={
          konecnaFaktura
            ? () => onDocumentDelete("invoice", konecnaFaktura.id)
            : undefined
        }
      />

      {/* ── Step 6: Odovzdávací protokol ─────────────────────────────────── */}
      <SingleStep
        state={s6}
        docType="contract_protocol"
        title="Odovzdávací protokol"
        subtitle="Odovzdávací protokol"
        number={odovzdavaciProtokol ? "Odovzdávací protokol" : undefined}
        date={odovzdavaciProtokol?.date}
        badgeLabel={
          s6 === "done" ? "Podpísaná" : s6 === "active" ? "Čaká sa" : undefined
        }
        isLast={true}
        menuKey="contract_protocol"
        openMenuKey={openMenuKey}
        onOpenMenuChange={setOpenMenuKey}
        onOpen={
          odovzdavaciProtokol
            ? () => onDocumentOpen("contract_protocol", odovzdavaciProtokol.id)
            : undefined
        }
        onDownload={
          odovzdavaciProtokol
            ? () =>
                onDocumentDownload("contract_protocol", odovzdavaciProtokol.id)
            : undefined
        }
        onEmail={
          odovzdavaciProtokol
            ? () =>
                onDocumentEmail("contract_protocol", odovzdavaciProtokol.id)
            : undefined
        }
        onDelete={
          odovzdavaciProtokol
            ? () =>
                onDocumentDelete("contract_protocol", odovzdavaciProtokol.id)
            : undefined
        }
      />
    </div>
      </div>{/* end left column */}

      {/* ── Right info panel ─────────────────────────────────────────────── */}
      <aside className="flex flex-col gap-3 w-full min-w-0 shrink-0">

        {/* Card 1: Aktívna ponuka */}
        {panelActiveOffer && (
          <div
            style={{
              background: "hsl(var(--color-background-secondary))",
              borderRadius: "var(--border-radius-lg)",
              padding: "1rem 1.125rem",
            }}
          >
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2.5">
              Aktívna ponuka
            </div>
            <div className="text-[14px] font-medium leading-tight">{panelActiveOffer.number}</div>
            {panelActiveOffer.description && (
              <div className="text-[13px] text-muted-foreground mt-0.5 leading-tight truncate">
                {panelActiveOffer.description}
              </div>
            )}
            <div className="mt-3 space-y-1.5">
              <div className="flex justify-between items-baseline text-[13px]">
                <span className="text-muted-foreground">Hodnota</span>
                <span className="font-medium">{fmtEur(panelActiveOffer.amount)}</span>
              </div>
              <div className="flex justify-between items-baseline text-[13px]">
                <span className="text-muted-foreground">Prijaté</span>
                <span>{fmt(panelActiveOffer.date)}</span>
              </div>
              <div className="flex justify-between items-baseline text-[13px]">
                <span className="text-muted-foreground">Ponuky</span>
                <span>
                  {offers.length} ks{declinedForPanel > 0 ? ` (${declinedForPanel} zamietnuté)` : ""}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Card 2: Postup */}
        <div
          style={{
            background: "hsl(var(--color-background-secondary))",
            borderRadius: "var(--border-radius-lg)",
            padding: "1rem 1.125rem",
          }}
        >
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2.5">
            Postup
          </div>
          <div className="flex justify-between items-baseline text-[13px] mb-2">
            <span className="text-muted-foreground">Splnené kroky</span>
            <span className="font-medium text-[#1D9E75]">{completedSteps} / 5</span>
          </div>
          <div
            className="w-full rounded-[3px] overflow-hidden"
            style={{
              height: 5,
              background: "hsl(var(--color-border-tertiary))",
            }}
          >
            <div
              className="h-full bg-[#aa3839] transition-all"
              style={{ width: `${(completedSteps / 5) * 100}%` }}
            />
          </div>
          <div className="mt-1.5 text-[11px] text-muted-foreground">
            Ďalší krok: {nextStepLabel[nextStep]}
          </div>
        </div>

        {/* Card 3: Ďalší krok — only when there is a next step and a wired callback (hidden if parent supplies nextStepSlot) */}
        {nextStep !== "completed" && ctaCallback && !nextStepSlot && (
          <div
            style={{
              background: "hsl(var(--color-background-secondary))",
              borderRadius: "var(--border-radius-lg)",
              padding: "1rem 1.125rem",
              border: "0.5px solid hsl(var(--color-border-tertiary))",
            }}
          >
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2.5">
              Ďalší krok
            </div>
            <button
              type="button"
              onClick={ctaCallback}
              className="w-full flex items-center gap-[10px] text-[13px] font-medium text-left transition-colors"
              style={{
                padding: "10px 12px",
                border: "0.5px solid hsl(var(--color-border-tertiary))",
                borderRadius: "var(--border-radius-md)",
                background: "hsl(var(--color-background-primary))",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "hsl(var(--color-background-secondary))";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background =
                  "hsl(var(--color-background-primary))";
              }}
            >
              {nextStepIcon[nextStep as Exclude<PipelineStep, "completed">]}
              Generovať {nextStepLabel[nextStep]}
            </button>
          </div>
        )}

        {nextStepSlot}
      </aside>
    </div>
    </div>
  );
}
